import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { IButton } from 'src/common/interfaces/IButton';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import { codeContainerStyles } from '../common/components/code-container/components/code-container-styles';
import barcode from '../common/media/barcode.svg';
import keypad from '../common/media/keypad.svg';
import transaction from '../common/media/Transactions.svg';

type Props = IBalanceInquiryProps;

export const showBalanceInquiryContainer = (props: Props, onScan: ScannedDataHandler): void => {
  setHomePage(<BalanceInquiryContainer {...props} />, onScan);
};

export const BalanceInquiryContainer: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { isProcessing } = useSelector((state: AppState) => state.balanceInquiry);
  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: props.onKeypadSubmit,
  };
  return (
    <ProductCodeContainer
      headerTitle={t('OnlineItemBalanceQueryCommand')}
      onKeypadSubmit={props.onKeypadSubmit}
      keypadComponent={
        <Keypad
          keyboardId='balance-inquiry'
          disableManualWeight
          disableQuantity
          topLabelText=' '
          submitButton={keypadSubmitButton}
          visible={!isProcessing}
          keypadTestIdPrefix='balance-inquiry-'
        />
      }
    >
      <BalanceInquiryScanWindow {...props} isProcessing={isProcessing} />
    </ProductCodeContainer>
  );
};

const BalanceInquiryScanWindow = (props: BalanceInquiryScanWindowProps): JSX.Element => {
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='balance-inquiry'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='balance-inquiry-scanning-container'
        >
          <Grid
            item
            container
            className={classes.flexDesign}
            data-testid='balance-inquiry-body-container'
          >
            {props.isProcessing && (
              <Grid item container className={classes.containerAlignment}>
                <Grid className={classes.spacing}>
                  <Typography variant={'h4'}>{t('ProcessingIndication')}</Typography>
                </Grid>
              </Grid>
            )}
            {!props.isProcessing && (
              <Grid item container className={classes.containerAlignment}>
                <Grid className={classes.spacing}>
                  <Typography variant={'h4'} data-testid='balance-inquiry-body-text1'>
                    {t('BalanceInquiryScanMessage1')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent='center'
                  className={classes.spacing}
                  data-testid='balance-inquiry-icon-1'
                >
                  <img src={transaction} alt='transaction' className={classes.icon} />
                </Grid>
                <Grid container justifyContent='center' className={classes.spacing}>
                  <Typography variant={'h4'} data-testid='balance-inquiry-body-text2'>
                    - {t('BalanceInquiryScanMessage2')} -
                  </Typography>
                </Grid>
                <Grid container justifyContent='center' className={classes.spacing}>
                  <Typography variant={'h4'} data-testid='balance-inquiry-body-text3'>
                    {t('BalanceInquiryScanMessage3')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent='center'
                  className={classes.spacing}
                  data-testid='barcode-pinpad-icon-container'
                >
                  <Grid className={classes.iconPadding}>
                    <img src={barcode} alt='Barcode' className={classes.icon} />
                  </Grid>
                  <img src={keypad} alt='keypad' className={classes.icon} />
                </Grid>
              </Grid>
            )}

            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                disabled={props.isProcessing}
                buttonType='white'
                onClick={props.onCancel}
                data-testid='balance-inquiry-cancel-button'
              >
                <Typography variant={'button'}>{t('Cancel')}</Typography>
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default BalanceInquiryScanWindow;

export interface BalanceInquiryScanWindowProps {
  onCancel: () => void;
  isProcessing: boolean;
}

export interface IBalanceInquiryProps {
  onCancel: () => void;
  onKeypadSubmit: (itemId: string) => void;
}
